/**
 * The workflow canvas (AMO-177): AmoregGTM's campaign canvas ported, drawing
 * an Eve workflow. It pans (drag the background, scroll) and zooms (Ctrl or
 * pinch, the controls), shows GTM's layered dot grid, and while editing:
 * steps drag out of the Steps panel (snapping onto an open outlet) or are
 * added after the selected step, cards drag with alignment snap, links are
 * drawn from a step's outlet dot or an open outlet's endpoint to another
 * step, and Delete removes the selected step or link. Ctrl (or Cmd) and a
 * click adds a step to the selection or takes it away, and Ctrl and a drag
 * across the background boxes several (`./selection.ts`); a group drags and
 * deletes together. Ctrl+Z and Ctrl+Y undo
 * and redo; Enter opens a step's panel and the arrow keys walk the chart.
 * Camera moves run on the power3-in-out curve, instantly when reduced
 * motion is asked for.
 *
 * The graph is the page's: every gesture is reported (`onAdd`, `onConnect`,
 * `onMove`, `onRemove`), never kept here. While steps are dragged they follow
 * the pointer here, and one move is reported when they are dropped.
 *
 * Others on the chart (AMO-178, `peers`) are drawn as GTM draws them
 * (`./presence.tsx`), and what this person does is reported for them
 * (`onPresence`): the pointer, the selected step, a step dragged, a link
 * drawn, a step brought out of the Steps panel. A step someone else is
 * dragging stays theirs until they let go: it can be selected, not moved.
 *
 * @module components/workflows/canvas/WorkflowCanvas
 */
import type {
  WorkflowGraph,
  WorkflowGraphIssue,
  WorkflowNode,
  WorkflowNodeType,
  WorkflowPeer,
  WorkflowPresenceState,
  WorkflowStepRun,
} from "@amoreg/contracts";
import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { workflowStepBranches } from "@repo/shared/workflowGraph";
import { useTheme } from "~/hooks/useTheme";
import { cn } from "~/lib/utils";
import { Spinner } from "../../ui/spinner";
import { isUndoChord } from "../workflowCanvas.logic";
import { type EdgeCues, type EdgePreview, CanvasEdges, portKey } from "./edges";
import {
  DEFAULT_VIEW,
  fitView as fitViewFor,
  freePlacement,
  hasInput,
  inputPoint,
  MAX_ZOOM,
  MIN_ZOOM,
  NODE_HEIGHT,
  NODE_WIDTH,
  nodeHeight,
  type OpenPort,
  openPorts,
  outputPoint,
  placementFor,
  type Point,
  type PortId,
  portsOf,
  snapToNodes,
  noticeSlot,
  stepInView,
  stepPanelInset,
  toWorld,
  type View,
  viewCentredOn,
  wheelZoomFactor,
  zoomAround,
} from "./geometry";
import { CanvasGrid, type GridPainter } from "./grid";
import { HOVER_CLOSE_MS, HOVER_OPEN_MS, hoverCardSide, StepHoverCard } from "./hoverCard";
import { kindClass } from "./kinds";
import { type CardCue, CanvasStep } from "./node";
import type { StepMark } from "../workflowChanges";
import { CanvasControls, Ghost, Palette } from "./panels";
import {
  PeerCursors,
  PeerFrames,
  PeerLinks,
  PeerNewSteps,
  useLiveCursors,
  useRemoteDrags,
} from "./presence";
import { canInsert, insertPlacement, type InsertTarget, insertTargetAt } from "./insertion";
import { heldByOther } from "./presenceTracks";
import { cueView, type DocSubscribe, NO_CUES, useRevisionCues } from "./revisionCues";
import { edgeRunStates } from "./runPath";
import { movedBy, NO_STEPS, stepsInBox, toggleStep, unionOf } from "./selection";
import { animate, prefersReducedMotion, type Tween } from "./tween";

type PaletteDrag = {
  readonly kind: WorkflowNodeType;
  readonly clientX: number;
  readonly clientY: number;
  readonly grabX: number;
  readonly grabY: number;
  readonly snap: OpenPort | null;
  /** Over a link's marker: the step goes in between its two steps. */
  readonly insert: InsertTarget | null;
  readonly placement: Point | null;
};
type LinkDrag = { readonly source: string; readonly port: PortId | null; readonly pointer: Point; readonly target: string | null };
type PointerEnd = (event: PointerEvent, cancelled: boolean) => void;
/** Steps being dragged: `id` is the one under the pointer, at `at`; the rest move by `delta` with it. */
type StepDrag = { readonly id: string; readonly at: Point; readonly delta: Point; readonly ids: ReadonlySet<string> };
/** The selection box, in pixels from the canvas's top left. */
type Marquee = { readonly left: number; readonly top: number; readonly width: number; readonly height: number };

const VIEW_DURATION = 250;
const SNAP_RADIUS = 48;
const DRAG_THRESHOLD = 4;
const ZOOM_STEP = 1.25;
const PANEL_INSET = 352;
/** The chart keeps clear of the Steps panel while a step still fits beside it (a narrow canvas, beside the builder's chat, too). */
const roomBesidePanel = (width: number) => width >= PANEL_INSET + NODE_WIDTH + 64;
/** Narrower than this (Ask eve open beside the chart), the Steps panel starts folded. */
const STEPS_BESIDE_MIN_WIDTH = 900;
const SNAP_TOLERANCE = 10;

function trackPointer(onMove: (event: PointerEvent) => void, onEnd: PointerEnd) {
  const handleUp = (event: PointerEvent) => {
    stop();
    onEnd(event, false);
  };
  const handleCancel = (event: PointerEvent) => {
    stop();
    onEnd(event, true);
  };
  function stop() {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", handleUp);
    window.removeEventListener("pointercancel", handleCancel);
  }
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", handleUp);
  window.addEventListener("pointercancel", handleCancel);
  return stop;
}

/** What a step's panel can ask of the canvas. */
export interface CanvasHelpers {
  readonly focusNode: (id: string) => void;
  /** A new step after `sourceId`, linked from its first free outlet; its panel opens. */
  readonly appendTo: (sourceId: string, kind: WorkflowNodeType) => void;
}

export type CanvasSelection = { readonly type: "node" | "edge"; readonly id: string } | null;
/** What Delete takes away: a link, or one or more steps. */
export type CanvasRemoval = { readonly type: "edge"; readonly id: string } | { readonly type: "nodes"; readonly ids: ReadonlyArray<string> };

export interface WorkflowCanvasProps {
  readonly label: string;
  readonly graph: WorkflowGraph;
  /** The chart can be changed (not a run, not shared to run only). */
  readonly editable: boolean;
  /** Shared with the reader to run only. */
  readonly locked: boolean;
  /** Each step's state on the run shown; null while editing. */
  readonly runSteps: ReadonlyMap<string, WorkflowStepRun> | null;
  readonly issues: ReadonlyMap<string, ReadonlyArray<WorkflowGraphIssue>>;
  readonly describe: (node: WorkflowNode) => string;
  readonly startPill: string;
  readonly now: number;
  /** The step whose panel is open. */
  readonly openId: string | null;
  readonly onOpen: (id: string | null) => void;
  /** A new step of `kind` at `at`, linked from `link`; its id, or null when refused. */
  readonly onAdd: (kind: WorkflowNodeType, at: Point, link: { readonly source: string; readonly port: PortId } | null) => string | null;
  /** A new step of `kind` at `at`, put into the link `edgeId` (what follows moves down by `push`); its id, or null when refused. */
  readonly onInsert: (kind: WorkflowNodeType, edgeId: string, at: Point, push: number) => string | null;
  readonly onConnect: (source: string, port: PortId, target: string) => void;
  /** Steps dropped in new places, all in one change. */
  readonly onMove: (positions: ReadonlyMap<string, Point>) => void;
  readonly onRemove: (removal: CanvasRemoval) => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly onTidy: (() => void) | null;
  readonly onRetry?: ((nodeId: string) => void) | undefined;
  readonly retrying?: string | null | undefined;
  /** Changes when the chart should fit itself again (another run, a tidy). */
  readonly fitKey: string;
  /** Receives the chart's keyboard shortcuts; the page calls it with the keys that are the chart's. */
  readonly keysRef: Ref<ChartKeys | null>;
  /** The chart is about to be laid out again (one saved before AMO-177): keep it covered. */
  readonly settling?: boolean | undefined;
  readonly renderInspector: (helpers: CanvasHelpers) => ReactNode;
  /** The body of the card beside a step the pointer rests on; null or absent for none. */
  readonly renderHoverCard?: ((node: WorkflowNode, helpers: { readonly close: () => void }) => ReactNode) | undefined;
  /** Top centre over the chart (a run's bar, a read-only note). */
  readonly notice?: ReactNode;
  /** Top right over the chart while no panel is open (the problems pill). */
  readonly status?: ReactNode;
  /** Everyone else on this chart (AMO-178). */
  readonly peers?: ReadonlyArray<WorkflowPeer>;
  /** What this person is doing, for the others. */
  readonly onPresence?: (patch: Partial<WorkflowPresenceState>) => void;
  /** The doc's steps, to play someone else's changes as cues; none without it, and none shown on a run. */
  readonly revisions?: DocSubscribe | undefined;
  /** Steps to bring into view together, once per `key` (a summary's Show). */
  readonly show?: { readonly key: number; readonly steps: ReadonlyArray<string> } | null | undefined;
  /** The steps eve or a colleague changed, as their cards show it. */
  readonly marks?: ReadonlyMap<string, StepMark> | undefined;
}

export type ChartKeys = (event: globalThis.KeyboardEvent) => void;

const NO_PEERS: ReadonlyArray<WorkflowPeer> = [];
const NO_ISSUES: ReadonlyArray<WorkflowGraphIssue> = [];
const NO_CANVAS_CUES = cueView(NO_CUES, "animate");
type Gesture = "pointer" | "wheel" | "pinch";
const WHEEL_IDLE_MS = 150;
const roundPoint = (point: Point): Point => ({ x: Math.round(point.x), y: Math.round(point.y) });

export function WorkflowCanvas({
  label,
  graph,
  editable,
  locked,
  runSteps,
  issues,
  describe,
  startPill,
  now,
  openId,
  onOpen,
  onAdd,
  onInsert,
  onConnect,
  onMove,
  onRemove,
  onUndo,
  onRedo,
  onTidy,
  onRetry,
  retrying = null,
  fitKey,
  keysRef,
  settling = false,
  renderInspector,
  renderHoverCard,
  notice,
  status,
  peers = NO_PEERS,
  onPresence,
  revisions,
  show = null,
  marks,
}: WorkflowCanvasProps) {
  const { resolvedTheme } = useTheme();
  const hasNotice = Boolean(notice);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const stopTrackingRef = useRef<(() => void) | null>(null);
  const tweenRef = useRef<Tween | null>(null);

  const [view, setViewState] = useState<View>(DEFAULT_VIEW);
  const viewRef = useRef<View>(DEFAULT_VIEW);
  const painterRef = useRef<GridPainter | null>(null);
  const [sizes, setSizes] = useState<Readonly<Record<string, number>>>({});
  const sizesRef = useRef(sizes);
  sizesRef.current = sizes;
  /** False until the first fit has drawn the measured chart; a cover hides it until then. */
  const [ready, setReady] = useState(false);
  const [selection, setSelection] = useState<CanvasSelection>(openId ? { type: "node", id: openId } : null);
  /**
   * The person's Steps panel choice, one for a chart with room beside it and
   * one for a narrower chart, where it starts folded. Neither a refit nor a
   * width change ever overwrites it.
   */
  const [stepsChoice, setStepsChoice] = useState({ wide: true, narrow: false });
  const [wide, setWide] = useState(true);
  const panelOpen = wide ? stepsChoice.wide : stepsChoice.narrow;
  const [palette, setPalette] = useState<PaletteDrag | null>(null);
  const [link, setLink] = useState<LinkDrag | null>(null);
  const [drag, setDrag] = useState<StepDrag | null>(null);
  const [panning, setPanning] = useState(false);
  /** Two or more steps selected together; empty while one step (`selection`) or none is. */
  const [group, setGroup] = useState<ReadonlySet<string>>(NO_STEPS);
  const [marquee, setMarquee] = useState<Marquee | null>(null);
  /** The step whose hover card shows; pinned once something in it is used, until the person is done. */
  const [hovered, setHovered] = useState<string | null>(null);
  const [hoverPinned, setHoverPinned] = useState(false);

  // The latest graph and selection, for handlers that outlive a render.
  const graphRef = useRef(graph);
  graphRef.current = graph;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const groupRef = useRef(group);
  groupRef.current = group;
  /** A step was just pressed: the focus that follows must not reselect it. */
  const pressedRef = useRef(false);
  const openRef = useRef(openId);
  openRef.current = openId;
  const peersRef = useRef(peers);
  peersRef.current = peers;
  const presenceRef = useRef(onPresence);
  presenceRef.current = onPresence;
  const cues = useRevisionCues(revisions);
  const cuesHidden = runSteps !== null;
  const shownCues = cuesHidden ? NO_CANVAS_CUES : cues.shown;
  const cuesHiddenRef = useRef(cuesHidden);
  cuesHiddenRef.current = cuesHidden;
  const { touch, viewMoved: stopFollowing } = cues;
  const touchStep = useCallback((id: string) => {
    if (!cuesHiddenRef.current) touch([id]);
  }, [touch]);
  const viewMoves = useRef(0);
  const viewMoved = useCallback(() => {
    viewMoves.current += 1;
    if (!cuesHiddenRef.current) stopFollowing();
  }, [stopFollowing]);
  const hoverPinnedRef = useRef(hoverPinned);
  hoverPinnedRef.current = hoverPinned;
  const hasHoverCard = Boolean(renderHoverCard);
  const hoverTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);
  const scheduleHover = useCallback((id: string | null, delay: number) => {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setHovered(id), delay);
  }, []);
  const closeHover = useCallback(() => {
    window.clearTimeout(hoverTimer.current);
    setHovered(null);
    setHoverPinned(false);
  }, []);
  // A card in use stays put; otherwise it follows the pointer onto a step and off it again.
  const onHoverStep = useCallback(
    (id: string, entering: boolean, pointerType: string) => {
      if (pointerType === "touch" || !hasHoverCard || hoverPinnedRef.current) return;
      scheduleHover(entering ? id : null, entering ? HOVER_OPEN_MS : HOVER_CLOSE_MS);
    },
    [hasHoverCard, scheduleHover],
  );

  const setView = useCallback((update: (current: View) => View) => {
    const next = update(viewRef.current);
    viewRef.current = next;
    painterRef.current?.(next);
    setViewState(next);
  }, []);

  // The selection follows an opened step; a step or link that is gone lets go.
  useEffect(() => {
    if (!openId) return;
    setSelection({ type: "node", id: openId });
    setGroup(NO_STEPS);
  }, [openId]);
  useEffect(() => {
    setSelection((current) => {
      if (!current) return current;
      const present = current.type === "node" ? graph.nodes.some((node) => node.id === current.id) : graph.edges.some((edge) => edge.id === current.id);
      return present ? current : null;
    });
    setGroup((current) => {
      if (current.size === 0) return current;
      const kept = new Set(graph.nodes.filter((node) => current.has(node.id)).map((node) => node.id));
      return kept.size === current.size ? current : kept.size > 1 ? kept : NO_STEPS;
    });
  }, [graph]);

  /** Selects `ids` together; one or none is an ordinary selection. `primary` stays selected when it is in. */
  const selectSteps = useCallback((ids: ReadonlySet<string>, primary: string | null) => {
    if (ids.size <= 1) {
      const [only] = ids;
      setGroup(NO_STEPS);
      setSelection(only ? { type: "node", id: only } : null);
      return;
    }
    setGroup(ids);
    setSelection(primary && ids.has(primary) ? { type: "node", id: primary } : null);
  }, []);
  /** The steps selected now: the group, else the one selected step. */
  const selectedSteps = useCallback((): ReadonlySet<string> => {
    if (groupRef.current.size > 0) return groupRef.current;
    const current = selectionRef.current;
    return current?.type === "node" ? new Set([current.id]) : NO_STEPS;
  }, []);

  // The step dragged is drawn where the pointer has it; one someone else drags, where they have it.
  const remoteDrags = useRemoteDrags(peers, graph.nodes);
  const cursors = useLiveCursors(peers);
  const pointing = useMemo(() => new Set(cursors.map(({ peer }) => peer.connection)), [cursors]);
  const nodes = useMemo(() => {
    if (!drag && remoteDrags.size === 0) return graph.nodes;
    return graph.nodes.map((node) => {
      if (drag?.ids.has(node.id)) return { ...node, position: { x: node.position.x + drag.delta.x, y: node.position.y + drag.delta.y } };
      const remote = remoteDrags.get(node.id);
      return remote ? { ...node, position: remote.shown } : node;
    });
  }, [drag, graph.nodes, remoteDrags]);
  const shown = useMemo(() => ({ ...graph, nodes }), [graph, nodes]);

  const bounds = useCallback(() => containerRef.current!.getBoundingClientRect(), []);
  const leftInset = useCallback((width: number) => (editable && panelOpen && roomBesidePanel(width) ? PANEL_INSET : 16), [editable, panelOpen]);
  const rightInset = useCallback((width: number) => (openRef.current ? stepPanelInset(width) : 16), []);

  // Before the first paint, so a narrow chart never shows the Steps panel for a frame.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    setWide(container.getBoundingClientRect().width >= STEPS_BESIDE_MIN_WIDTH);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWide(entry.contentRect.width >= STEPS_BESIDE_MIN_WIDTH);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const stopTween = useCallback(() => {
    tweenRef.current?.stop();
    tweenRef.current = null;
  }, []);

  const gesture = useRef<{ readonly moves: number; readonly by: Set<Gesture> } | null>(null);
  const heldFrame = useRef<string | null>(null);
  const frameMove = useRef<{ readonly tween: Tween; readonly step: string; readonly ends: number } | null>(null);
  const focusNodeRef = useRef<(id: string) => void>(() => undefined);
  const frameStep = useCallback((id: string) => {
    if (gesture.current) {
      heldFrame.current = id;
      return;
    }
    const before = tweenRef.current;
    focusNodeRef.current(id);
    const tween = tweenRef.current;
    frameMove.current = tween && tween !== before ? { tween, step: id, ends: performance.now() + VIEW_DURATION } : null;
  }, []);
  const beginGesture = useCallback((by: Gesture) => {
    gesture.current ??= { moves: viewMoves.current, by: new Set() };
    gesture.current.by.add(by);
    const moving = frameMove.current;
    frameMove.current = null;
    if (moving && moving.tween === tweenRef.current && performance.now() < moving.ends) {
      stopTween();
      heldFrame.current = moving.step;
    }
  }, [stopTween]);
  const endGesture = useCallback((by: Gesture) => {
    const ended = gesture.current;
    ended?.by.delete(by);
    if (ended?.by.size) return;
    const held = heldFrame.current;
    gesture.current = null;
    heldFrame.current = null;
    if (ended && held && viewMoves.current === ended.moves) frameStep(held);
  }, [frameStep]);

  const track = useCallback(
    (onMoveEvent: (event: PointerEvent) => void, onEnd: PointerEnd) => {
      stopTrackingRef.current?.();
      beginGesture("pointer");
      stopTrackingRef.current = trackPointer(onMoveEvent, (event, cancelled) => {
        stopTrackingRef.current = null;
        onEnd(event, cancelled);
        endGesture("pointer");
      });
    },
    [beginGesture, endGesture],
  );

  const animateView = useCallback(
    (update: (current: View) => View) => {
      stopTween();
      const from = viewRef.current;
      const to = update(from);
      if (prefersReducedMotion()) {
        setView(() => to);
        return;
      }
      tweenRef.current = animate(VIEW_DURATION, (progress) =>
        setView(() => ({ x: from.x + (to.x - from.x) * progress, y: from.y + (to.y - from.y) * progress, zoom: from.zoom + (to.zoom - from.zoom) * progress })),
      );
    },
    [setView, stopTween],
  );

  const cancelInteraction = useCallback(() => {
    stopTrackingRef.current?.();
    stopTrackingRef.current = null;
    endGesture("pointer");
    setPalette(null);
    setLink(null);
    setDrag(null);
    setPanning(false);
    setMarquee(null);
  }, [endGesture]);

  useEffect(
    () => () => {
      stopTrackingRef.current?.();
      tweenRef.current?.stop();
    },
    [],
  );

  // What this person does, for the others on the chart.
  const selectedStep = selection?.type === "node" ? selection.id : null;
  useEffect(() => presenceRef.current?.({ selection: selectedStep }), [selectedStep]);
  useEffect(() => presenceRef.current?.({ drag: drag ? { id: drag.id, at: drag.at } : null }), [drag]);
  useEffect(
    () =>
      presenceRef.current?.({
        link: link ? { source: link.source, port: link.port, pointer: roundPoint(link.pointer), target: link.target } : null,
      }),
    [link],
  );
  const adding = palette?.placement ? { kind: palette.kind, at: roundPoint(palette.placement) } : null;
  const addingKey = adding ? `${adding.kind}:${adding.at.x}:${adding.at.y}` : null;
  useEffect(
    () => presenceRef.current?.({ adding }),
    // `adding` is new every render; its key says when it changed.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [addingKey],
  );
  const shareCursor = useCallback(
    (event: ReactPointerEvent) => {
      if (!presenceRef.current || !containerRef.current) return;
      presenceRef.current({ cursor: roundPoint(toWorld(event.clientX, event.clientY, bounds(), viewRef.current)) });
    },
    [bounds],
  );

  /** Brings a step into view between the Steps panel and the open panel, when it is not. */
  const reveal = useCallback(
    (node: WorkflowNode) => {
      if (!containerRef.current) return;
      const rect = bounds();
      const height = nodeHeight(node, sizesRef.current);
      const area = { width: rect.width, height: rect.height, left: leftInset(rect.width), right: rightInset(rect.width) };
      if (stepInView(node, height, viewRef.current, area)) return;
      animateView((value) => ({ ...value, ...viewCentredOn(node, height, value.zoom, area) }));
    },
    [animateView, bounds, leftInset, rightInset],
  );

  const focusNode = useCallback(
    (id: string) => {
      const node = graphRef.current.nodes.find((item) => item.id === id);
      if (node) reveal(node);
    },
    [reveal],
  );
  focusNodeRef.current = focusNode;
  const framed = useRef<number | null>(null);
  const frame = cues.shown.frame;
  useEffect(() => {
    const first = frame?.steps[0];
    if (!frame || !first || framed.current === frame.key) return;
    if (cuesHidden) {
      framed.current = frame.key;
      return;
    }
    const request = window.requestAnimationFrame(() => {
      framed.current = frame.key;
      frameStep(first);
    });
    return () => window.cancelAnimationFrame(request);
  }, [cuesHidden, frame, frameStep]);
  /**
   * Brings a step into view once the page has drawn what was just asked
   * (a panel opened takes the right side, so the step must clear it).
   */
  const focusSoon = useCallback((id: string) => window.requestAnimationFrame(() => focusNode(id)), [focusNode]);

  const fit = useCallback(
    (options: { readonly animated?: boolean; readonly minZoom?: number; readonly only?: ReadonlyArray<string> } = {}) => {
      if (!containerRef.current) return;
      const rect = bounds();
      const { only } = options;
      const target = fitViewFor(
        only ? graphRef.current.nodes.filter((node) => only.includes(node.id)) : graphRef.current.nodes,
        sizesRef.current,
        // Below a pill over the chart (a run's bar), beside the Steps panel.
        {
          width: rect.width,
          height: rect.height,
          left: editable && panelOpen && roomBesidePanel(rect.width) ? PANEL_INSET : 0,
          right: openRef.current && rect.width >= 1100 ? stepPanelInset(rect.width) : 0,
          top: hasNotice ? (noticeSlot({ width: rect.width, steps: !editable ? "none" : panelOpen ? "open" : "folded", stepPanel: openRef.current !== null })?.bottom ?? 0) : 0,
        },
        options.minZoom ?? MIN_ZOOM,
      );
      if (!target) return;
      if (options.animated === false) setView(() => target);
      else animateView(() => target);
    },
    [animateView, bounds, editable, hasNotice, panelOpen, setView],
  );

  // On opening (and on each new fit key): fit, never below 80% so the words stay readable.
  // The steps' heights are measured after the first frame, so the first time
  // the chart fits once more a frame later and only then lifts the cover.
  useEffect(() => {
    let second = 0;
    const first = requestAnimationFrame(() => {
      fit({ animated: false, minZoom: 0.8 });
      const open = openRef.current ? graphRef.current.nodes.find((node) => node.id === openRef.current) : undefined;
      if (open) window.setTimeout(() => reveal(open), 60);
      if (ready || settling) return;
      second = requestAnimationFrame(() => {
        fit({ animated: false, minZoom: 0.8 });
        setReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only on a new fit key, or once the chart has settled
  }, [fitKey, settling]);

  const showed = useRef<number | null>(null);
  useEffect(() => {
    if (!show || showed.current === show.key) return;
    showed.current = show.key;
    fit({ only: show.steps });
  }, [show, fit]);

  const createNode = useCallback(
    (kind: WorkflowNodeType, at: Point, source: { readonly source: string; readonly port: PortId } | null) => {
      if (!editable) return null;
      const id = onAdd(kind, { x: Math.round(at.x), y: Math.round(at.y) }, source);
      if (id) setSelection({ type: "node", id });
      return id;
    },
    [editable, onAdd],
  );

  /** A new step put into a link, between the two steps it joins; it is selected. */
  const insertNode = useCallback(
    (kind: WorkflowNodeType, edgeId: string) => {
      if (!editable) return null;
      const current = graphRef.current;
      const edge = current.edges.find((item) => item.id === edgeId);
      const between = edge ? insertPlacement(current, sizesRef.current, edge) : null;
      if (!between) return null;
      const id = onInsert(kind, edgeId, between.at, between.push);
      if (id) {
        setGroup(NO_STEPS);
        setSelection({ type: "node", id });
      }
      return id;
    },
    [editable, onInsert],
  );

  /** The new step's place and link when added from the panel: after the selected step, else the first open outlet. */
  const addToNextSlot = useCallback(
    (kind: WorkflowNodeType) => {
      const current = graphRef.current;
      const measured = sizesRef.current;
      const selected = selectionRef.current?.type === "node" ? current.nodes.find((node) => node.id === selectionRef.current?.id) : undefined;
      const ports = openPorts(current, measured);
      let slot: { readonly source: WorkflowNode; readonly port: PortId } | null = null;
      if (selected && portsOf(selected.type).length > 0) {
        const free = ports.find((port) => port.source.id === selected.id);
        // A single outlet takes another link (they run side by side); a Condition's two must have one free.
        const port = free?.port ?? (workflowStepBranches(selected.type) ? null : "out");
        if (port) slot = { source: selected, port };
      } else if (!selected) {
        const first = ports[0];
        const source = first ? current.nodes.find((node) => node.id === first.source.id) : undefined;
        if (first && source) slot = { source, port: first.port };
      }
      const at = slot
        ? freePlacement(placementFor(slot.source, slot.port, measured), current.nodes, measured)
        : (() => {
            const rect = bounds();
            const center = toWorld(rect.left + rect.width / 2, rect.top + rect.height / 3, rect, viewRef.current);
            return freePlacement({ x: center.x - NODE_WIDTH / 2, y: center.y }, current.nodes, measured);
          })();
      const id = createNode(kind, at, slot ? { source: slot.source.id, port: slot.port } : null);
      if (id) focusSoon(id);
    },
    [bounds, createNode, focusSoon],
  );

  const appendTo = useCallback(
    (sourceId: string, kind: WorkflowNodeType) => {
      const current = graphRef.current;
      const source = current.nodes.find((node) => node.id === sourceId);
      if (!source || portsOf(source.type).length === 0) return;
      const port = openPorts(current, sizesRef.current).find((item) => item.source.id === sourceId)?.port ?? (workflowStepBranches(source.type) ? "yes" : "out");
      const id = createNode(kind, freePlacement(placementFor(source, port, sizesRef.current), current.nodes, sizesRef.current), { source: sourceId, port });
      if (!id) return;
      onOpen(id);
      focusSoon(id);
    },
    [createNode, focusSoon, onOpen],
  );

  const findSnap = useCallback((world: Point, zoom: number) => {
    const current = graphRef.current;
    const measured = sizesRef.current;
    let best: { readonly port: OpenPort; readonly placement: Point } | null = null;
    let bestDistance = Infinity;
    for (const port of openPorts(current, measured)) {
      const placement = placementFor(port.source, port.port, measured);
      const distance = Math.hypot(world.x - port.point.x, world.y - port.point.y) * zoom;
      const inside = world.x >= placement.x && world.x <= placement.x + NODE_WIDTH && world.y >= port.point.y - 12 && world.y <= placement.y + NODE_HEIGHT;
      if ((distance <= SNAP_RADIUS || inside) && distance < bestDistance) {
        best = { port, placement: freePlacement(placement, current.nodes, measured) };
        bestDistance = distance;
      }
    }
    return best;
  }, []);

  const resolvePalette = useCallback(
    (kind: WorkflowNodeType, clientX: number, clientY: number, grabX: number, grabY: number): PaletteDrag => {
      const base: PaletteDrag = { kind, clientX, clientY, grabX, grabY, snap: null, insert: null, placement: null };
      const rect = bounds();
      const panel = panelRef.current?.getBoundingClientRect();
      const overPanel = panel && clientX >= panel.left && clientX <= panel.right && clientY >= panel.top && clientY <= panel.bottom;
      const inside = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
      if (overPanel || !inside) return base;
      const current = viewRef.current;
      const world = toWorld(clientX, clientY, rect, current);
      // Only right on a link's marker does the step go in between; anywhere else it is placed as usual.
      const insert = insertTargetAt(graphRef.current, sizesRef.current, kind, world, current.zoom);
      const between = insert ? insertPlacement(graphRef.current, sizesRef.current, insert.edge) : null;
      if (insert && between) return { ...base, insert, placement: between.at };
      const snap = findSnap(world, current.zoom);
      if (snap) return { ...base, snap: snap.port, placement: snap.placement };
      return { ...base, placement: { x: world.x - NODE_WIDTH / 2, y: world.y - 18 } };
    },
    [bounds, findSnap],
  );

  const handleItemPointerDown = useCallback(
    (kind: WorkflowNodeType, event: ReactPointerEvent) => {
      if (event.button !== 0) return;
      // The press is the canvas's own drag: without this the browser goes on to its native drag, carrying
      // any text selected on the page (seen as a faint image of the whole page under the pointer).
      event.preventDefault();
      window.getSelection()?.removeAllRanges();
      const item = event.currentTarget.getBoundingClientRect();
      const grabX = event.clientX - item.left;
      const grabY = event.clientY - item.top;
      const startX = event.clientX;
      const startY = event.clientY;
      let active = false;
      track(
        (move) => {
          if (!active && Math.hypot(move.clientX - startX, move.clientY - startY) < DRAG_THRESHOLD) return;
          active = true;
          setPalette(resolvePalette(kind, move.clientX, move.clientY, grabX, grabY));
        },
        (end, cancelled) => {
          setPalette(null);
          if (cancelled) return;
          if (!active) {
            addToNextSlot(kind);
            return;
          }
          const drop = resolvePalette(kind, end.clientX, end.clientY, grabX, grabY);
          if (!drop.placement) return;
          if (drop.insert) {
            insertNode(kind, drop.insert.edge.id);
            return;
          }
          createNode(kind, drop.placement, drop.snap ? { source: drop.snap.source.id, port: drop.snap.port } : null);
        },
      );
    },
    [addToNextSlot, createNode, insertNode, resolvePalette, track],
  );

  const handleNodePointerDown = useCallback(
    (id: string, event: ReactPointerEvent) => {
      if (event.button !== 0) return;
      event.stopPropagation();
      closeHover();
      touchStep(id);
      // The press's own focus event comes before any timer; a later (keyboard) focus is not the press's.
      pressedRef.current = true;
      window.setTimeout(() => {
        pressedRef.current = false;
      }, 0);
      // Ctrl (or Cmd) and a click: in or out of the selection, nothing dragged.
      if ((event.ctrlKey || event.metaKey) && editable) {
        const next = toggleStep(selectedSteps(), id);
        selectSteps(next, next.has(id) ? id : (selectionRef.current?.id ?? null));
        return;
      }
      // Pressing a step of the group keeps the group, to drag it; else only this step is selected.
      const inGroup = groupRef.current.has(id);
      if (!inGroup) setGroup(NO_STEPS);
      if (selectionRef.current?.id !== id) setSelection({ type: "node", id });
      if (!editable) return;
      const node = graphRef.current.nodes.find((item) => item.id === id);
      const ids = inGroup ? groupRef.current : new Set([id]);
      // Someone else is dragging one of them: it stays theirs until they let go.
      if (!node || [...ids].some((item) => heldByOther(peersRef.current, item))) return;
      const zoom = viewRef.current.zoom;
      const startX = event.clientX;
      const startY = event.clientY;
      let active = false;
      let delta: Point = { x: 0, y: 0 };
      track(
        (move) => {
          const deltaX = move.clientX - startX;
          const deltaY = move.clientY - startY;
          if (!active) {
            if (Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return;
            active = true;
          }
          // The step under the pointer snaps to the steps left standing; the rest keep their places beside it.
          const measured = sizesRef.current;
          const moving = { x: node.position.x + deltaX / zoom, y: node.position.y + deltaY / zoom, width: NODE_WIDTH, height: nodeHeight(node, measured) };
          const others = graphRef.current.nodes
            .filter((item) => !ids.has(item.id))
            .map((item) => ({ x: item.position.x, y: item.position.y, width: NODE_WIDTH, height: nodeHeight(item, measured) }));
          const snapped = snapToNodes(moving, others, SNAP_TOLERANCE / zoom);
          delta = { x: Math.round(snapped.x) - node.position.x, y: Math.round(snapped.y) - node.position.y };
          setDrag({ id, at: { x: node.position.x + delta.x, y: node.position.y + delta.y }, delta, ids });
        },
        (_event, cancelled) => {
          setDrag(null);
          if (cancelled) return;
          // A click on one step of a group selects that step alone.
          if (!active) {
            if (inGroup) selectSteps(new Set([id]), id);
            return;
          }
          if (delta.x !== 0 || delta.y !== 0) onMove(movedBy(graphRef.current.nodes, ids, delta));
        },
      );
    },
    [closeHover, editable, onMove, selectSteps, selectedSteps, touchStep, track],
  );

  const nodeAt = useCallback((world: Point, exclude: string) => {
    const all = graphRef.current.nodes;
    for (let index = all.length - 1; index >= 0; index--) {
      const node = all[index]!;
      if (node.id === exclude || !hasInput(node.type)) continue;
      if (
        world.x >= node.position.x &&
        world.x <= node.position.x + NODE_WIDTH &&
        world.y >= node.position.y - 16 &&
        world.y <= node.position.y + nodeHeight(node, sizesRef.current)
      )
        return node.id;
    }
    return null;
  }, []);

  const startLink = useCallback(
    (source: string, port: PortId | null, event: ReactPointerEvent) => {
      if (event.button !== 0 || !editable) return;
      event.stopPropagation();
      const measure = (clientX: number, clientY: number): LinkDrag => {
        const pointer = toWorld(clientX, clientY, bounds(), viewRef.current);
        return { source, port, pointer, target: nodeAt(pointer, source) };
      };
      // From a Condition's outlet dot: Yes or No by the way the drag first heads,
      // as its stubs do (Yes down to the left, No down to the right), whatever
      // side the link is dropped on. A stub's endpoint names its own outlet.
      const startX = event.clientX;
      let branch: PortId | null = null;
      const portOf = (dragged: LinkDrag): PortId => {
        if (dragged.port) return dragged.port;
        const sourceNode = graphRef.current.nodes.find((node) => node.id === source);
        if (!sourceNode || !workflowStepBranches(sourceNode.type)) return "out";
        return branch ?? "yes";
      };
      setLink(measure(event.clientX, event.clientY));
      track(
        (move) => {
          if (branch === null && Math.abs(move.clientX - startX) >= DRAG_THRESHOLD) branch = move.clientX < startX ? "yes" : "no";
          setLink(measure(move.clientX, move.clientY));
        },
        (end, cancelled) => {
          setLink(null);
          if (cancelled) return;
          const result = measure(end.clientX, end.clientY);
          if (!result.target || result.target === source) return;
          onConnect(source, portOf(result), result.target);
        },
      );
    },
    [bounds, editable, nodeAt, onConnect, track],
  );

  const handleHandlePointerDown = useCallback((id: string, event: ReactPointerEvent) => startLink(id, null, event), [startLink]);
  const handleEndpointPointerDown = useCallback((source: string, port: PortId, event: ReactPointerEvent) => startLink(source, port, event), [startLink]);
  const handleEdgePointerDown = useCallback((id: string, event: ReactPointerEvent) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    setGroup(NO_STEPS);
    setSelection({ type: "edge", id });
  }, []);

  /** Ctrl and a drag across the background: a box that adds every step it touches to the selection. */
  const startMarquee = (event: ReactPointerEvent) => {
    const rect = bounds();
    const view = viewRef.current;
    const start = toWorld(event.clientX, event.clientY, rect, view);
    const before = selectedSteps();
    const primary = selectionRef.current?.type === "node" ? selectionRef.current.id : null;
    const startX = event.clientX;
    const startY = event.clientY;
    let active = false;
    track(
      (move) => {
        if (!active) {
          if (Math.hypot(move.clientX - startX, move.clientY - startY) < DRAG_THRESHOLD) return;
          active = true;
        }
        setMarquee({
          left: Math.min(startX, move.clientX) - rect.left,
          top: Math.min(startY, move.clientY) - rect.top,
          width: Math.abs(move.clientX - startX),
          height: Math.abs(move.clientY - startY),
        });
        const end = toWorld(move.clientX, move.clientY, rect, view);
        selectSteps(unionOf(before, stepsInBox(graphRef.current.nodes, sizesRef.current, start, end)), primary);
      },
      () => setMarquee(null),
    );
  };

  const handleCanvasPointerDown = (event: ReactPointerEvent) => {
    if (event.button !== 0 && event.button !== 1) return;
    stopTween();
    closeHover();
    if (event.button === 0 && (event.ctrlKey || event.metaKey) && editable) {
      startMarquee(event);
      return;
    }
    const from = viewRef.current;
    const startX = event.clientX;
    const startY = event.clientY;
    let active = false;
    track(
      (move) => {
        const deltaX = move.clientX - startX;
        const deltaY = move.clientY - startY;
        if (!active) {
          if (Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return;
          active = true;
          setPanning(true);
          viewMoved();
        }
        setView(() => ({ ...from, x: from.x + deltaX, y: from.y + deltaY }));
      },
      () => {
        setPanning(false);
        if (active) return;
        setSelection(null);
        setGroup(NO_STEPS);
      },
    );
  };

  // Scroll pans; Ctrl (or a trackpad pinch) zooms about the pointer.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    let wheelEnd = 0;
    const handleWheel = (event: WheelEvent) => {
      if (event.target instanceof Element && event.target.closest("[data-canvas-overlay]")) return;
      event.preventDefault();
      stopTween();
      beginGesture("wheel");
      window.clearTimeout(wheelEnd);
      wheelEnd = window.setTimeout(() => endGesture("wheel"), WHEEL_IDLE_MS);
      viewMoved();
      const scale = event.deltaMode === 1 ? 16 : 1;
      const rect = element.getBoundingClientRect();
      if (event.ctrlKey || event.metaKey) {
        const factor = wheelZoomFactor(event.deltaY, event.deltaMode);
        setView((current) => zoomAround(current, current.zoom * factor, event.clientX - rect.left, event.clientY - rect.top, rect.width));
        return;
      }
      // Shift turns a mouse wheel sideways (some browsers already report it as deltaX).
      const sideways = event.shiftKey && event.deltaX === 0;
      const deltaX = sideways ? event.deltaY : event.deltaX;
      const deltaY = sideways ? 0 : event.deltaY;
      setView((current) => ({ ...current, x: current.x - deltaX * scale, y: current.y - deltaY * scale }));
    };
    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.clearTimeout(wheelEnd);
      endGesture("wheel");
      element.removeEventListener("wheel", handleWheel);
    };
  }, [beginGesture, endGesture, setView, stopTween, viewMoved]);

  // Two-finger pinch on touch screens: zoom about the midpoint.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    let pinch: { distance: number; zoom: number } | null = null;
    const pair = (event: TouchEvent) => [event.touches[0]!, event.touches[1]!] as const;
    const onStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      beginGesture("pinch");
      cancelInteraction();
      viewMoved();
      const [a, b] = pair(event);
      pinch = { distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), zoom: viewRef.current.zoom };
    };
    const onTouchMove = (event: TouchEvent) => {
      if (!pinch || event.touches.length !== 2) return;
      event.preventDefault();
      const [a, b] = pair(event);
      const rect = element.getBoundingClientRect();
      const zoom = pinch.zoom * (Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) / pinch.distance);
      setView((current) => zoomAround(current, zoom, (a.clientX + b.clientX) / 2 - rect.left, (a.clientY + b.clientY) / 2 - rect.top, rect.width));
    };
    const onEnd = () => {
      if (pinch) endGesture("pinch");
      pinch = null;
    };
    element.addEventListener("touchstart", onStart, { passive: true });
    element.addEventListener("touchmove", onTouchMove, { passive: false });
    element.addEventListener("touchend", onEnd);
    element.addEventListener("touchcancel", onEnd);
    return () => {
      element.removeEventListener("touchcancel", onEnd);
      element.removeEventListener("touchstart", onStart);
      element.removeEventListener("touchmove", onTouchMove);
      element.removeEventListener("touchend", onEnd);
    };
  }, [beginGesture, cancelInteraction, endGesture, setView, viewMoved]);

  const removeSelected = useCallback(() => {
    const current = selectionRef.current;
    if (!editable) return;
    if (current?.type === "edge") {
      onRemove({ type: "edge", id: current.id });
      setSelection(null);
      return;
    }
    // Start can never be removed; the rest of a group goes.
    const steps = selectedSteps();
    const ids = graphRef.current.nodes.filter((node) => steps.has(node.id) && node.type !== "start").map((node) => node.id);
    if (ids.length === 0) return;
    onRemove({ type: "nodes", ids });
    setSelection(null);
    setGroup(NO_STEPS);
    if (openRef.current && ids.includes(openRef.current)) onOpen(null);
  }, [editable, onOpen, onRemove, selectedSteps]);

  useImperativeHandle(
    keysRef,
    () => (event: globalThis.KeyboardEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest("input, textarea, select, [contenteditable='true'], [role=dialog], [role=menu], [role=listbox]")) return;
      const key = event.key.toLowerCase();
      if (isUndoChord(event) && editable) {
        event.preventDefault();
        if (key === "y" || event.shiftKey) onRedo();
        else onUndo();
        return;
      }
      if ((key === "delete" || key === "backspace") && (selectionRef.current || groupRef.current.size > 0) && editable) {
        event.preventDefault();
        removeSelected();
        return;
      }
      if (key === "escape") {
        cancelInteraction();
        closeHover();
        if (openRef.current) onOpen(null);
        else {
          setSelection(null);
          setGroup(NO_STEPS);
        }
      }
    },
    [cancelInteraction, closeHover, editable, onOpen, onRedo, onUndo, removeSelected],
  );

  const zoomBy = (factor: number) => {
    viewMoved();
    const rect = bounds();
    animateView((current) => zoomAround(current, current.zoom * factor, rect.width / 2, rect.height / 2, rect.width));
  };

  const openNode = useCallback(
    (id: string) => {
      closeHover();
      touchStep(id);
      onOpen(id);
      focusSoon(id);
    },
    [closeHover, focusSoon, onOpen, touchStep],
  );

  const onFocusNode = useCallback(
    (id: string) => {
      // Focus that follows a press: the press has already chosen what is selected.
      const pressed = pressedRef.current;
      pressedRef.current = false;
      if (!pressed) {
        if (!groupRef.current.has(id)) setGroup(NO_STEPS);
        if (selectionRef.current?.id !== id) setSelection({ type: "node", id });
        touchStep(id);
      }
      focusNode(id);
    },
    [focusNode, touchStep],
  );

  // Keyboard on a step: Enter opens it, Down and Up follow its links, Left and Right its neighbours.
  const onKeyNav = useCallback(
    (id: string, event: KeyboardEvent) => {
      const current = graphRef.current;
      const focus = (targetId: string | undefined) => {
        if (!targetId) return;
        event.preventDefault();
        containerRef.current?.querySelector<HTMLElement>(`[data-node="${CSS.escape(targetId)}"] [role=button]`)?.focus({ preventScroll: true });
      };
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openNode(id);
        return;
      }
      const node = current.nodes.find((item) => item.id === id);
      if (!node) return;
      if (event.key === "ArrowDown") focus(current.edges.find((edge) => edge.source === id)?.target);
      if (event.key === "ArrowUp") focus(current.edges.find((edge) => edge.target === id)?.source);
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const direction = event.key === "ArrowLeft" ? -1 : 1;
        const candidates = current.nodes
          .filter((other) => other.id !== id && Math.abs(other.position.y - node.position.y) < 160 && Math.sign(other.position.x - node.position.x) === direction)
          .sort((a, b) => Math.abs(a.position.x - node.position.x) - Math.abs(b.position.x - node.position.x));
        focus(candidates[0]?.id);
      }
    },
    [openNode],
  );

  const onMeasure = useCallback((id: string, height: number) => {
    setSizes((current) => (current[id] === height ? current : { ...current, [id]: height }));
  }, []);

  // ---- What is drawn ----
  const snapKey = palette?.snap ? portKey(palette.snap.source.id, palette.snap.port) : null;
  let preview: EdgePreview | null = null;
  const linkSource = link ? nodes.find((node) => node.id === link.source) : undefined;
  if (link && linkSource) {
    const target = link.target ? nodes.find((node) => node.id === link.target) : undefined;
    preview = {
      from: outputPoint(linkSource, nodeHeight(linkSource, sizes)),
      to: target ? inputPoint(target) : link.pointer,
      fromKind: linkSource.type,
      toKind: target?.type ?? null,
    };
  } else if (palette?.snap && palette.placement) {
    const source = palette.snap.source;
    const sourceNode = nodes.find((node) => node.id === source.id);
    if (sourceNode) {
      preview = {
        from: outputPoint(sourceNode, nodeHeight(sourceNode, sizes)),
        to: { x: palette.placement.x + NODE_WIDTH / 2, y: palette.placement.y },
        fromKind: sourceNode.type,
        toKind: palette.kind,
      };
    }
  }

  const cardCues = useMemo(() => {
    const cards = new Map<string, CardCue>();
    for (const [id, cue] of shownCues.steps) {
      cards.set(id, { ...cue, motion: shownCues.motion, descriptionBefore: cue.glow ? describe(cue.glow.before) : null });
    }
    return cards;
  }, [describe, shownCues]);
  const edgeCues = useMemo((): EdgeCues | null => {
    const moving = new Set([...shownCues.steps].flatMap(([id, cue]) => (cue.moving ? [id] : [])));
    return shownCues.travelling.size === 0 && moving.size === 0 ? null : { travelling: shownCues.travelling, moving };
  }, [shownCues]);

  // On a run: the links it went down are lit, one into a running step flows, one it cannot take fades.
  const runStates = useMemo(() => (runSteps ? edgeRunStates(graph, runSteps) : null), [graph, runSteps]);

  const canvasStyle = { "--view-x": `${view.x}px`, "--view-y": `${view.y}px`, "--view-zoom": view.zoom } as CSSProperties;
  const interacting = Boolean(palette || link || drag || panning || marquee);
  // Not while something is being dragged, nor for a step whose panel is already open.
  const hoverNode = hovered && hovered !== openId && !interacting ? nodes.find((node) => node.id === hovered) : undefined;
  const hoverBody = hoverNode && renderHoverCard ? renderHoverCard(hoverNode, { close: closeHover }) : null;

  return (
    <div
      ref={containerRef}
      role="application"
      aria-roledescription="workflow canvas"
      aria-label={label}
      style={canvasStyle}
      onPointerDown={handleCanvasPointerDown}
      onPointerMove={shareCursor}
      onPointerLeave={() => presenceRef.current?.({ cursor: null })}
      onScroll={(event) => {
        // Focus can scroll an overflow-hidden box; the canvas only ever pans.
        event.currentTarget.scrollTop = 0;
        event.currentTarget.scrollLeft = 0;
      }}
      className="workflow-canvas relative min-h-0 flex-1 touch-none overflow-hidden bg-canvas-panel select-none"
      data-workflow-canvas=""
      data-workflow-zoom={view.zoom.toFixed(2)}
    >
      <CanvasGrid viewRef={viewRef} painterRef={painterRef} theme={resolvedTheme} />

      <div className="absolute top-0 left-1/2 origin-top-left [transform:translate(var(--view-x),var(--view-y))_scale(var(--view-zoom))]">
        <CanvasEdges
          graph={shown}
          sizes={sizes}
          selectedEdgeId={selection?.type === "edge" ? selection.id : null}
          snapKey={snapKey}
          preview={preview}
          runStates={runStates}
          editable={editable}
          insertOptions={Boolean(palette && canInsert(palette.kind))}
          insertEdgeId={palette?.insert?.edge.id ?? null}
          cues={edgeCues}
          onEdgePointerDown={handleEdgePointerDown}
          onEndpointPointerDown={handleEndpointPointerDown}
          onDeleteEdge={removeSelected}
        />

        {palette?.snap && palette.placement ? (
          <div
            key={snapKey}
            aria-hidden
            style={{ translate: `${palette.placement.x}px ${palette.placement.y}px`, width: NODE_WIDTH, height: NODE_HEIGHT }}
            className={cn(
              kindClass(palette.kind),
              "pointer-events-none absolute top-0 left-0 rounded-lg border border-dashed border-kind/40 bg-kind/5 transition-opacity duration-150 ease-power3-out motion-reduce:transition-none starting:opacity-0",
            )}
          />
        ) : null}

        {revisions
          ? createPortal(
              <div aria-live="polite" className="sr-only" data-workflow-announcer="">
                {cues.shown.announcement ? <p key={cues.shown.announcement.key}>{cues.shown.announcement.text}</p> : null}
              </div>,
              document.body,
            )
          : null}

        {shownCues.ghosts.map(({ node, key }) => (
          <div
            key={`ghost-${node.id}-${key}`}
            aria-hidden
            data-workflow-ghost={node.id}
            style={{ translate: `${node.position.x}px ${node.position.y}px`, width: NODE_WIDTH, height: nodeHeight(node, sizes) }}
            className={cn(
              kindClass(node.type),
              "pointer-events-none absolute top-0 left-0 rounded-lg border border-dashed border-kind/50 bg-kind/5 px-4 py-3 text-[14px] leading-5 font-semibold text-kind/60",
              shownCues.motion === "animate" && "animate-canvas-cue-ghost",
            )}
          >
            {node.label}
          </div>
        ))}

        {nodes.map((node) => (
          <CanvasStep
            key={node.id}
            node={node}
            description={describe(node)}
            selected={group.has(node.id) || (selection?.type === "node" && selection.id === node.id)}
            grouped={group.has(node.id)}
            targeted={link?.target === node.id}
            dragging={Boolean(drag?.ids.has(node.id)) || remoteDrags.has(node.id)}
            editing={openId === node.id}
            hoverOpen={Boolean(hoverBody) && hoverNode?.id === node.id}
            editable={editable}
            locked={locked}
            issues={runSteps ? NO_ISSUES : (issues.get(node.id) ?? NO_ISSUES)}
            step={runSteps?.get(node.id) ?? null}
            now={now}
            startPill={startPill}
            onRetry={onRetry}
            retrying={retrying === node.id}
            onMeasure={onMeasure}
            onNodePointerDown={handleNodePointerDown}
            onHandlePointerDown={handleHandlePointerDown}
            onOpen={openNode}
            onKeyNav={onKeyNav}
            onFocusNode={onFocusNode}
            onHover={onHoverStep}
            cue={cardCues.get(node.id)}
            mark={marks?.get(node.id)}
          />
        ))}

        {hoverNode && hoverBody ? (
          <StepHoverCard
            key={hoverNode.id}
            node={hoverNode}
            zoom={view.zoom}
            height={nodeHeight(hoverNode, sizes)}
            side={(() => {
              const width = containerRef.current?.clientWidth ?? 0;
              const left = width / 2 + view.x + hoverNode.position.x * view.zoom;
              return hoverCardSide(left, left + NODE_WIDTH * view.zoom, leftInset(width), width - rightInset(width));
            })()}
            onMore={() => openNode(hoverNode.id)}
            onPointerEnter={() => window.clearTimeout(hoverTimer.current)}
            onPointerLeave={() => {
              if (!hoverPinnedRef.current) scheduleHover(null, HOVER_CLOSE_MS);
            }}
            onEngage={() => setHoverPinned(true)}
          >
            {hoverBody}
          </StepHoverCard>
        ) : null}

        {peers.length > 0 ? (
          <>
            <PeerFrames
              peers={peers}
              nodes={nodes}
              sizes={sizes}
              drags={remoteDrags}
              pointing={pointing}
            />
            <PeerLinks peers={peers} graph={shown} sizes={sizes} />
            <PeerNewSteps peers={peers} pointing={pointing} />
            <PeerCursors shown={cursors} />
          </>
        ) : null}
      </div>

      {editable ? (
        <Palette
          ref={panelRef}
          open={panelOpen}
          draggingKind={palette?.kind ?? null}
          onToggle={() => setStepsChoice((choice) => (wide ? { ...choice, wide: !choice.wide } : { ...choice, narrow: !choice.narrow }))}
          onItemPointerDown={handleItemPointerDown}
          onAdd={addToNextSlot}
        />
      ) : null}

      {notice ? (
        <NoticeRow steps={!editable ? "none" : panelOpen ? "open" : "folded"} stepPanel={openId !== null}>
          {notice}
        </NoticeRow>
      ) : null}

      {status && !openId ? (
        <div
          data-canvas-overlay
          onPointerDown={(event) => event.stopPropagation()}
          className="pointer-events-none absolute top-4 right-4 z-20 flex flex-col items-end gap-2"
        >
          {status}
        </div>
      ) : null}

      {renderInspector({ focusNode: focusSoon, appendTo })}

      <CanvasControls
        canZoomIn={view.zoom < MAX_ZOOM - 0.001}
        canZoomOut={view.zoom > MIN_ZOOM + 0.001}
        onZoomIn={() => zoomBy(ZOOM_STEP)}
        onZoomOut={() => zoomBy(1 / ZOOM_STEP)}
        onFit={() => {
          viewMoved();
          fit();
        }}
        onTidy={editable ? onTidy : null}
      />

      {marquee ? (
        <div
          aria-hidden
          data-workflow-marquee=""
          style={marquee}
          className="pointer-events-none absolute z-10 rounded-sm border border-primary/60 bg-primary/10"
        />
      ) : null}

      {interacting ? <div aria-hidden className={cn("fixed inset-0 z-40", link || marquee ? "cursor-crosshair" : "cursor-grabbing")} /> : null}
      {palette ? <Ghost kind={palette.kind} x={palette.clientX - palette.grabX} y={palette.clientY - palette.grabY} snapped={Boolean(palette.snap || palette.insert)} /> : null}

      {/* Hides the chart while it lays itself out and fits, then fades away. */}
      <div
        aria-hidden={ready || undefined}
        data-workflow-canvas-loading={ready ? undefined : ""}
        className={cn(
          "pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-canvas-panel transition-opacity duration-200 motion-reduce:transition-none",
          ready && "opacity-0",
        )}
      >
        {ready ? null : <Spinner className="size-5 text-muted-foreground" />}
      </div>
    </div>
  );
}


function NoticeRow({
  steps,
  stepPanel,
  children,
}: {
  readonly steps: "none" | "folded" | "open";
  readonly stepPanel: boolean;
  readonly children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  // The canvas is the row's own parent: measured from here, it is attached when the row first lays out.
  useLayoutEffect(() => {
    const canvas = ref.current?.parentElement;
    if (!canvas) return;
    setWidth(canvas.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);
  const slot = noticeSlot({ width, steps, stepPanel });
  return (
    <div
      ref={ref}
      data-canvas-overlay
      onPointerDown={(event) => event.stopPropagation()}
      className="pointer-events-none absolute z-20 flex flex-col items-center gap-2"
      style={slot === null ? { display: "none" } : { left: slot.left, right: slot.right, top: slot.top }}
      data-workflow-notice-row=""
    >
      {children}
    </div>
  );
}
